import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LocationContext = createContext(null);
const STORAGE_KEY = "curtron_share_location";
const PING_INTERVAL_MS = 30000; // throttle server pings to at most every 30s

export function LocationProvider({ children }) {
  const { user } = useAuth();
  // Default ON — dispatch needs to see drivers. Users can opt out in Profile.
  const [enabled, setEnabledState] = useState(() => localStorage.getItem(STORAGE_KEY) !== "0");
  const [permission, setPermission] = useState("unknown"); // granted | denied | prompt | unknown | unsupported
  const [sharing, setSharing] = useState(false);
  const [lastPingAt, setLastPingAt] = useState(null);
  const watchId = useRef(null);
  const lastSent = useRef(0);

  const setEnabled = useCallback((val) => {
    setEnabledState(val);
    localStorage.setItem(STORAGE_KEY, val ? "1" : "0");
  }, []);

  // Track the browser's geolocation permission state.
  useEffect(() => {
    if (!("geolocation" in navigator)) { setPermission("unsupported"); return; }
    if (!navigator.permissions?.query) return;
    let ps;
    navigator.permissions.query({ name: "geolocation" }).then((status) => {
      ps = status;
      setPermission(status.state);
      status.onchange = () => setPermission(status.state);
    }).catch(() => {});
    return () => { if (ps) ps.onchange = null; };
  }, []);

  const sendPing = useCallback((pos) => {
    const now = Date.now();
    if (now - lastSent.current < PING_INTERVAL_MS) return;
    lastSent.current = now;
    const { latitude, longitude, accuracy } = pos.coords;
    api.post("/location/ping", { lat: latitude, lng: longitude, accuracy: accuracy != null ? Math.round(accuracy) : undefined })
      .then(() => setLastPingAt(new Date()))
      .catch((e) => console.debug("location ping failed", e?.response?.status));
  }, []);

  const stopWatch = useCallback(() => {
    if (watchId.current != null) { navigator.geolocation.clearWatch(watchId.current); watchId.current = null; }
    setSharing(false);
  }, []);

  const startWatch = useCallback(() => {
    if (!("geolocation" in navigator) || watchId.current != null) return;
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => { setSharing(true); setPermission("granted"); sendPing(pos); },
      (err) => { if (err.code === 1) setPermission("denied"); setSharing(false); },
      { enableHighAccuracy: true, maximumAge: 20000, timeout: 27000 }
    );
  }, [sendPing]);

  // Explicit request (user gesture) — triggers the native permission prompt.
  const requestLocation = useCallback(() => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => { setPermission("granted"); setEnabled(true); sendPing(pos); startWatch(); },
      (err) => { if (err.code === 1) setPermission("denied"); },
      { enableHighAccuracy: true, timeout: 27000 }
    );
  }, [sendPing, startWatch, setEnabled]);

  // Start/stop the watcher based on auth + toggle + permission.
  useEffect(() => {
    if (user && enabled && permission === "granted") startWatch();
    else stopWatch();
    return stopWatch;
  }, [user, enabled, permission, startWatch, stopWatch]);

  return (
    <LocationContext.Provider value={{ enabled, setEnabled, permission, sharing, lastPingAt, requestLocation }}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocationShare() {
  return useContext(LocationContext);
}
